import { StringHelper } from "../Helpers/StringHelper";
import { $$, IJNativeEvent } from "../JNative";
import { INavigationQueueItem } from "./INavigationQueueItem";
import { IPageManager } from "./PageManager";
import { IPage } from "./Pages/Page";
import { NAMESPACE } from "./_Namespace";

export const
    ANIMATION_END_EVENTS = "webkitAnimationEnd oAnimationEnd oanimationend animationend msAnimationEnd",
    ANIMATION_NAME_DEFAULT = "bouncy",
    TRANSITION_END_EVENTS = "webkitTransitionEnd oTransitionEnd otransitionend transitionend msTransitionEnd";

export interface IPageTransitioner
{
    executeAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem): Promise<unknown>;
}

export class PageTransitioner implements IPageTransitioner
{
    public static classNamespace = NAMESPACE + "PageTransitioner";
    public static className = "PageTransitioner";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Public Methods

    public executeAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        switch (navigationQueueItem.transitionType)
        {
            case "animation":
                return this._executePageAnimationAsync(pageManager, navigationQueueItem);
            case "transition":
                return this._executePageTransitionAsync(pageManager, navigationQueueItem);
            default:
                return this._executePageAnimationAsync(pageManager, navigationQueueItem);
        }
    }

    // #endregion

    // #region Protected Methods

    protected _animatePageAsync(page: IPage, options: { animationName: string, direction: string, reverse?: boolean, hide?: boolean })
    {
        const
            animationClass = options.animationName + " " + options.direction + (options.reverse ? " reverse" : StringHelper.empty),
            $$page = page.$$element.get();

        if (!$$page)
            return Promise.reject();

        $$("body").addClass("rad-state-page-animating");

        return new Promise(resolve =>
        {
            if (!$$page)
            {
                resolve(undefined);
                return;
            }

            $$page
                .removeClass(options.animationName + " in out reverse")
                .on(ANIMATION_END_EVENTS, function ()
                {
                    $$page!.off(ANIMATION_END_EVENTS);
                    $$("body").removeClass("rad-state-page-animating");
                    $$page!.removeClass(options.animationName + " in out reverse");

                    if (options.hide)
                        page.hide();

                    resolve(undefined);
                })
                .addClass(animationClass);
        });
    }
    protected _executePageAnimationAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        const
            animationName = navigationQueueItem.transitionName ?? ANIMATION_NAME_DEFAULT,
            currentPageOrNull = pageManager.currentPage.get();

        let
            animatedNewPageIn = false;

        return new Promise((resolve, reject) =>
        {
            if (!currentPageOrNull)
            {
                reject();
                return;
            }

            const
                currentPage = currentPageOrNull!;

            this._animatePageAsync(currentPage, { animationName: animationName!, direction: "out", reverse: navigationQueueItem.back ?? false, hide: true })
                .then(() =>
                {
                    if (!navigationQueueItem.page)
                        return Promise.reject();

                    if (!navigationQueueItem.page)
                        return Promise.reject();

                    navigationQueueItem.page.$$element.get()?.appendTo(pageManager.$$pageContainer);
                    navigationQueueItem.page.$$element.get()?.css("display", StringHelper.empty);
                    navigationQueueItem.page.$$element.get()?.addClass("rad-state-invisible");
                    navigationQueueItem.page.show();

                    if (!(navigationQueueItem.page as any)[".loaded"])
                    {
                        return (
                            navigationQueueItem.page.loadAsync()
                                .then(() =>
                                {
                                    if (!navigationQueueItem.page)
                                        return Promise.reject();

                                    (navigationQueueItem.page as any)[".loaded"] = true;
                                    return navigationQueueItem.page.activateAsync();
                                })
                        );
                    }

                    return navigationQueueItem.page.activateAsync();
                })
                .then(() =>
                {
                    if (!navigationQueueItem.page)
                        return Promise.reject();

                    if (!navigationQueueItem.page)
                        return Promise.reject();

                    navigationQueueItem.page.$$element.get()?.removeClass("rad-state-invisible");
                    return this._animatePageAsync(navigationQueueItem.page, { animationName: animationName, direction: "in", reverse: navigationQueueItem.back });
                })
                .then(() =>
                {
                    animatedNewPageIn = true;
                    resolve(undefined);
                })
                .catch(error =>
                {
                    if (!animatedNewPageIn)
                    {
                        const
                            $$currentPage = currentPage.$$element.get();

                        if (!$$currentPage)
                            reject(error);
                        else
                        {
                            navigationQueueItem.page?.$$element.get()?.css("display", "none");
                            navigationQueueItem.page?.$$element.get()?.addClass("rad-state-invisible");
                            navigationQueueItem.page?.hide();

                            $$currentPage.appendTo(pageManager.$$pageContainer);
                            $$currentPage.css("display", StringHelper.empty);
                            $$currentPage.removeClass("rad-state-invisible");
                            currentPage.show();

                            this._animatePageAsync(currentPage, { animationName: animationName, direction: "in", reverse: true })
                                .finally(() =>
                                {
                                    reject(error);
                                });
                        }

                        return;
                    }

                    reject(error);
                });
        });
    }
    protected _executePageTransitionAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        switch (navigationQueueItem.transitionName)
        {
            case "fade":
                return this._executePageTransitionFadeAsync(pageManager, navigationQueueItem);
            case "slide":
                return this._executePageTransitionSlideAsync(pageManager, navigationQueueItem);
            default:
                return this._executePageTransitionDefaultAsync(pageManager, navigationQueueItem);
        }
    }
    protected _executePageTransitionDefaultAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        const
            currentPage = pageManager.currentPage.get();

        if (!currentPage)
            return Promise.reject();

        const
            $$currentPage = currentPage.$$element.get();

        if (!$$currentPage)
            return Promise.reject();

        let
            pageLoadAndOrActivatePromise;

        if (!navigationQueueItem.page)
            return Promise.reject();

        if (!navigationQueueItem.page)
            return Promise.reject();

        navigationQueueItem.page.$$element.get()?.appendTo(pageManager.$$pageContainer);
        navigationQueueItem.page.$$element.get()?.css("display", StringHelper.empty);
        navigationQueueItem.page.$$element.get()?.addClass("rad-state-invisible");
        navigationQueueItem.page.show();

        if (!(navigationQueueItem.page as any)[".loaded"])
        {
            pageLoadAndOrActivatePromise =
                navigationQueueItem.page.loadAsync()
                    .then(() =>
                    {
                        if (!navigationQueueItem.page)
                            return Promise.reject();

                        (navigationQueueItem.page as any)[".loaded"] = true;
                        return navigationQueueItem.page.activateAsync();
                    });
        }
        else
            pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

        return (
            (pageLoadAndOrActivatePromise ?? Promise.resolve())
                .then(() =>
                {
                    if (!navigationQueueItem.page)
                        return Promise.reject();

                    $$currentPage.css("display", "none");
                    currentPage.hide();

                    navigationQueueItem.page.$$element.get()?.removeClass("rad-state-invisible");
                })
                .catch(error =>
                {
                    navigationQueueItem.page?.$$element.get()?.css("display", "none");
                    navigationQueueItem.page?.$$element.get()?.addClass("rad-state-invisible");
                    navigationQueueItem.page?.hide();

                    $$currentPage.appendTo(pageManager.$$pageContainer);
                    $$currentPage.css("display", StringHelper.empty);
                    $$currentPage.removeClass("rad-state-invisible");
                    currentPage.show();

                    throw error;
                })
        );
    }
    protected _executePageTransitionFadeAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        const
            currentPage = pageManager.currentPage.get();

        if (!currentPage)
            return Promise.reject();

        const
            $$currentPage = currentPage.$$element.get();

        if (!$$currentPage)
            return Promise.reject();

        return new Promise((resolve, reject) =>
        {
            if (!navigationQueueItem.page)
            {
                reject();
                return;
            }

            if (!navigationQueueItem.page)
            {
                reject();
                return;
            }

            let
                pageLoadAndOrActivatePromise;

            navigationQueueItem.page.$$element.get()?.appendTo(pageManager.$$pageContainer);
            navigationQueueItem.page.$$element.get()?.css("display", StringHelper.empty);
            navigationQueueItem.page.$$element.get()?.css({ opacity: 0, transition: StringHelper.empty });
            navigationQueueItem.page.show();

            function currentPageTransitionEnd(this: any, evt: IJNativeEvent)
            {
                if (evt.target !== this)
                    return;

                $$currentPage?.off(TRANSITION_END_EVENTS, currentPageTransitionEnd);

                $$currentPage?.css({
                    display: "none",
                    opacity: StringHelper.empty,
                    transition: StringHelper.empty
                });

                currentPage?.hide();

                navigationQueueItem.page?.$$element.get()?.on(TRANSITION_END_EVENTS, newPageTransitionEnd);
                navigationQueueItem.page?.$$element.get()?.css({ transition: "opacity 0.25s ease-out" });
                setTimeout(() => { navigationQueueItem.page?.$$element.get()?.css({ opacity: 1 }); });
            }

            function newPageTransitionEnd(this: any, evt: IJNativeEvent)
            {
                if (evt.target !== this)
                    return;

                navigationQueueItem.page?.$$element.get()?.off(TRANSITION_END_EVENTS, newPageTransitionEnd);

                navigationQueueItem.page?.$$element.get()?.css({ opacity: StringHelper.empty, transition: StringHelper.empty });
                resolve(undefined);
            }

            if (!(navigationQueueItem.page as any)[".loaded"])
            {
                pageLoadAndOrActivatePromise =
                    navigationQueueItem.page.loadAsync()
                        .then(() =>
                        {
                            if (!navigationQueueItem.page)
                                return Promise.reject();

                            (navigationQueueItem.page as any)[".loaded"] = true;
                            return navigationQueueItem.page.activateAsync();
                        });
            }
            else
                pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

            pageLoadAndOrActivatePromise
                .then(() =>
                {
                    $$currentPage.on(TRANSITION_END_EVENTS, currentPageTransitionEnd);
                    $$currentPage.css({ transition: "opacity 0.25s ease-out" });
                    setTimeout(() => { $$currentPage.css({ opacity: 0 }); });
                })
                .catch(error =>
                {
                    navigationQueueItem.page?.$$element.get()?.css("display", "none");
                    navigationQueueItem.page?.hide();

                    $$currentPage.appendTo(pageManager.$$pageContainer);
                    $$currentPage.css({ display: StringHelper.empty });
                    currentPage.show();

                    pageManager.$$pageContainer
                        .css({
                            left: "0px",
                            transition: StringHelper.empty
                        });

                    reject(error);
                });
        });
    }
    protected _executePageTransitionSlideAsync(pageManager: IPageManager, navigationQueueItem: INavigationQueueItem)
    {
        const
            currentPage = pageManager.currentPage.get();

        if (!currentPage)
            return Promise.reject();

        const
            $$currentPage = currentPage.$$element.get();

        if (!$$currentPage)
            return Promise.reject();

        return new Promise((resolve, reject) =>
        {
            if (!navigationQueueItem.page)
            {
                reject();
                return;
            }

            if (!navigationQueueItem.page)
            {
                reject();
                return;
            }

            let
                pageLoadAndOrActivatePromise;

            if (navigationQueueItem.back)
                navigationQueueItem.page.$$element.get()?.insertBefore($$currentPage);
            else
                navigationQueueItem.page.$$element.get()?.appendTo(pageManager.$$pageContainer);

            navigationQueueItem.page.$$element.get()?.css("display", StringHelper.empty);
            navigationQueueItem.page.show();

            pageManager.$$pageContainer
                .css({
                    left: navigationQueueItem.back ? "-100%" : "0%",
                    transition: StringHelper.empty
                });

            // Force repaint
            pageManager.$$pageContainer[0].offsetHeight;

            function transitionEnd(this: any, evt: IJNativeEvent)
            {
                if (evt.target !== this)
                    return;

                pageManager.$$pageContainer.off(TRANSITION_END_EVENTS, transitionEnd);

                $$currentPage?.css("display", "none");
                currentPage?.hide();

                if (navigationQueueItem.back)
                    navigationQueueItem.page?.$$element.get()?.appendTo(pageManager.$$pageContainer);

                pageManager.$$pageContainer
                    .css({
                        left: "0%",
                        transition: StringHelper.empty
                    });

                resolve(undefined);
            }

            if (!(navigationQueueItem.page as any)[".loaded"])
            {
                pageLoadAndOrActivatePromise =
                    navigationQueueItem.page.loadAsync()
                        .then(() =>
                        {
                            if (!navigationQueueItem.page)
                                return Promise.reject();

                            (navigationQueueItem.page as any)[".loaded"] = true;
                            return navigationQueueItem.page.activateAsync();
                        });
            }
            else
                pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

            pageLoadAndOrActivatePromise
                .then(() =>
                {
                    pageManager.$$pageContainer
                        .css({ transition: "left 0.25s ease-out" })
                        .on(TRANSITION_END_EVENTS, transitionEnd)
                        .css({ left: navigationQueueItem.back ? "0%" : "-100%" });
                })
                .catch(error =>
                {
                    navigationQueueItem.page?.$$element.get()?.css("display", "none");
                    navigationQueueItem.page?.hide();

                    $$currentPage.appendTo(pageManager.$$pageContainer);
                    $$currentPage.css("display", StringHelper.empty);
                    currentPage.show();

                    pageManager.$$pageContainer
                        .css({
                            left: "0%",
                            transition: StringHelper.empty
                        });

                    reject(error);
                });
        });
    }

    // #endregion
}